import React from 'react';
import { z } from 'zod';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// About build — faithful rewrite of legacy molecules/AboutBuild (Strapi
// sections.about-new-build, e.g. "Building Together"). Centered header over a
// four-up row of image cards, each with a white caption strip at the bottom.
// Mobile scrolls the cards horizontally.
export const aboutBuildSchema = z.object({
  eyebrow: z.string().optional(),
  heading: z.string().min(1),
  description: z.string().optional(),
  cards: z
    .array(
      z.object({
        title: z.string().min(1),
        imageUrl: z.string().optional(),
      }),
    )
    .min(1),
});

type AboutBuildProps = z.infer<typeof aboutBuildSchema>;

const Band = styled.section`
  background: #fcfaf5;
`;

const Inner = styled.div`
  max-width: 112rem;
  margin: 0 auto;
  padding: 4rem 0;

  @media (min-width: 48em) {
    padding: 12rem 0;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 1.6rem;
`;

const Eyebrow = styled.p`
  margin: 0;
  color: #1a1b1f;
  font-family: 'DM Sans', sans-serif;
  font-weight: 500;
  font-size: 1.6rem;
  letter-spacing: 5px;
  text-transform: uppercase;
  text-align: center;

  @media (min-width: 48em) {
    font-size: 1.8rem;
  }
`;

const Heading = styled.h2`
  margin: 0;
  color: #1a1b1f;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: 2.8rem;
  letter-spacing: -2px;
  text-align: center;
  max-width: 32rem;

  @media (min-width: 48em) {
    font-size: 5.6rem;
    max-width: 54.8rem;
  }
`;

const Description = styled.p`
  margin: 0.8rem 0 0;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-weight: 400;
  font-size: 1.6rem;
  line-height: 1.6;
  text-align: center;
  max-width: 36rem;

  @media (min-width: 48em) {
    font-size: 1.8rem;
    max-width: 54.8rem;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 20rem;
  gap: 2.4rem;
  overflow-x: auto;
  margin-top: 2rem;
  padding: 0 1.6rem;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  @media (min-width: 48em) {
    grid-auto-flow: row;
    grid-auto-columns: unset;
    grid-template-columns: repeat(4, 1fr);
    overflow: visible;
    margin-top: 6rem;
    padding: 0;
  }
`;

const Card = styled.div`
  position: relative;
  border-radius: 1rem;

  @media (min-width: 48em) {
    border-radius: 2rem;
  }
`;

const CardImage = styled.img`
  display: block;
  width: 100%;
  height: 40rem;
  object-fit: contain;
`;

const Caption = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  background: #ffffff;
  border-radius: 8px 8px 10px 10px;
  box-shadow: 0px -1px 0px 0px #b1c1e7 inset;
  padding: 1.6rem;

  @media (min-width: 48em) {
    border-radius: 8px 8px 20px 20px;
    padding: 2.4rem;
  }
`;

const CaptionText = styled.p`
  margin: 0;
  color: #1a1b1f;
  font-family: 'DM Sans', sans-serif;
  font-weight: 500;
  font-size: 1.4rem;

  @media (min-width: 48em) {
    font-size: 2rem;
    line-height: 2.8rem;
  }
`;

function AboutBuild({ eyebrow, heading, description, cards }: AboutBuildProps) {
  return (
    <Band>
      <Inner>
        <Header>
          {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
          <Heading>{heading}</Heading>
          {description ? <Description>{description}</Description> : null}
        </Header>
        <Grid>
          {cards.map((card, i) => (
            <Card key={`${card.title}-${i}`}>
              {card.imageUrl ? <CardImage src={card.imageUrl} alt="" /> : null}
              <Caption>
                <CaptionText>{card.title}</CaptionText>
              </Caption>
            </Card>
          ))}
        </Grid>
      </Inner>
    </Band>
  );
}

export const aboutBuildBlock: BlockDefinition<AboutBuildProps> = {
  type: 'aboutBuild',
  schema: aboutBuildSchema,
  Renderer: AboutBuild,
};
